import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@/hooks/useWallet';
import { useGameState } from '@/hooks/useGameState';
import { useContract } from '@/hooks/useContract';
import { useGameContext } from '@/contexts/GameContext';
import WalletButton from '@/components/web3/WalletButton';
import NetworkBadge from '@/components/web3/NetworkBadge';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { GameCanvas, useGameEngine } from '@/engine/GameCanvas';
import { WorldScene } from '@/engine/WorldScene';
import { BattleScene, BattleResult, PartyMember } from '@/engine/BattleScene';
import { MenuScene, MenuStateResult } from '@/engine/MenuScene';
import { TownScene } from '@/engine/TownScene';
import { DungeonScene } from '@/engine/DungeonScene';
import { SpriteManager } from '@/engine/SpriteManager';
import { GameScene } from '@/engine/types/engine';
import { GameEngine } from '@/engine/GameEngine';
import { CLASS_INFO, Character, GameState } from '@/lib/types/game';
import { BattleEnemy } from '@/engine/types/engine';
import { getItem } from '@/engine/data/ItemData';
import { getOwnedKeyItems, addKeyItem } from '@/engine/data/KeyItemData';
import { getTownById, Building, BuildingType } from '@/engine/data/TownData';
import { Location as StoryLocation, StoryFlag } from '@/engine/data/StoryData';
import { QuestManager } from '@/engine/QuestManager';
import { ShopScene, ShopItem } from '@/engine/ShopScene';

// Enemy name lookup (matches EnemyData.sol IDs)
const ENEMY_NAMES: Record<number, string> = {
  1: 'Goblin', 2: 'Wolf', 3: 'Zombie', 4: 'Skeleton', 5: 'Imp',
  6: 'Ogre', 7: 'GrOgre', 8: 'Ghost', 9: 'Cobra', 10: 'Spider',
  11: 'Warg', 12: 'Ghoul', 13: 'Wraith', 14: 'Mummy', 15: 'Specter',
  16: 'Minotaur', 17: 'Gargoyle', 18: 'Manticore', 19: 'Chimera', 20: 'Vampire',
  36: 'Garland', 37: 'Pirate', 38: 'Astos', 39: 'Lich', 40: 'Kary',
  41: 'Kraken', 42: 'Tiamat', 43: 'Chaos',
};

// Convert enemy ID to BattleEnemy object
const enemyIdToBattleEnemy = (id: number, index: number): BattleEnemy => ({
  id,
  name: ENEMY_NAMES[id] || `Enemy ${id}`,
  hp: 50 + id * 10, // Placeholder HP based on ID
  maxHP: 50 + id * 10,
  attack: 10 + id * 2, // Attack scales with ID
  defense: 5 + id, // Defense scales with ID
  magicDefense: 10 + id * 2, // Magic defense scales with ID
  agility: 5 + id, // Agility scales with ID
  x: 60 + (index % 3) * 60,
  y: 40 + Math.floor(index / 3) * 50,
  spriteId: `enemy_${id}`,
  dead: false,
});

// Starting spells by class (FF1-authentic)
// White Mage Level 1: CURE (1), HARM (2), FOG (3), RUSE (4)
// Black Mage Level 1: FIRE (33), LIT (34), SLEP (35), LOCK (36)
// Red Mage Level 1: CURE (1), FIRE (33)
const getStartingSpells = (classType: number): number[] => {
  switch (classType) {
    case 4: // White Mage
    case 10: // White Wizard
      return [1, 2, 3, 4]; // CURE, HARM, FOG, RUSE
    case 5: // Black Mage
    case 11: // Black Wizard
      return [33, 34, 35, 36]; // FIRE, LIT, SLEP, LOCK
    case 3: // Red Mage
    case 9: // Red Wizard
      return [1, 33]; // CURE, FIRE
    default:
      return []; // Fighter, Thief, Monk - no spells
  }
};

// Get starting spell charges by class
// NOTE: The SpellSystem expects charges stored by SPELL ID, not spell level
// Each spell gets its own charge counter (FF1-authentic: level-based pooling
// would require SpellSystem refactor, so we give per-spell charges for now)
const getStartingSpellCharges = (classType: number): Map<number, number> => {
  const charges = new Map<number, number>();
  const spells = getStartingSpells(classType);

  // Give 3 charges per spell (reasonable starting amount)
  spells.forEach(spellId => {
    charges.set(spellId, 3);
  });

  return charges;
};

// Convert game Character to BattleScene PartyMember format
const characterToPartyMember = (char: Character, index: number): PartyMember => ({
  id: index,
  name: CLASS_INFO[char.class]?.name || `Hero ${index + 1}`,
  class: char.class as any, // Map game class to battle class
  level: char.level,
  hp: char.currentHP,
  maxHP: char.maxHP,
  mp: char.currentMP,
  maxMP: char.maxMP,
  intelligence: char.intelligence,
  spirit: char.intelligence, // FF1 uses INT for both
  status: char.status === 0 ? 'OK' : 'Status',
  experience: char.experience || 0,
  learnedSpells: getStartingSpells(char.class),
  spellCharges: getStartingSpellCharges(char.class),
});

// Convert BattleScene PartyMember back to game Character (after battle)
const partyMemberToCharacter = (member: PartyMember, original: Character): Character => ({
  ...original,
  level: member.level,
  experience: member.experience || original.experience,
  currentHP: member.hp,
  maxHP: member.maxHP,
  currentMP: member.mp,
  maxMP: member.maxMP,
  // Note: Other stats (STR, AGI, etc.) would be updated during level up
});

// Convert battle inventory Map to GameState arrays
const mapToInventoryArrays = (map: Map<number, number>): { inventory: number[], inventoryQty: number[] } => {
  const inventory: number[] = [];
  const inventoryQty: number[] = [];

  map.forEach((qty, id) => {
    if (qty > 0) {
      inventory.push(id);
      inventoryQty.push(qty);
    }
  });

  // Pad arrays to 64 slots (GameState format)
  while (inventory.length < 64) {
    inventory.push(0);
    inventoryQty.push(0);
  }

  return { inventory, inventoryQty };
};

interface SlotData {
  active: boolean;
  state: GameState | null;
}

const Game: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected } = useWallet();
  const { gameState, combatState, isLoading, hasGame, isInCombat, refreshState, hasAttemptedFetch } = useGameState();
  const { setGameState } = useGameContext();
  const { saveGameToChain, getSlotInfo, getGameStateForSlot, selectSlot, isLoading: isSaving } = useContract();
  const { engine, handleEngineReady, isReady: isEngineReady } = useGameEngine();
  const scenesRegistered = useRef(false);
  const spriteManagerRef = useRef<SpriteManager | null>(null);
  const questManagerRef = useRef<QuestManager | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [livePosition, setLivePosition] = useState<{ x: number; y: number; scene: string }>({ x: 0, y: 0, scene: 'World' });
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showHomeConfirm, setShowHomeConfirm] = useState(false);
  const [slots, setSlots] = useState<SlotData[]>([]);
  const [currentSlot, setCurrentSlot] = useState<number>(0);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  // Track if we've done initial position sync from gameState (to avoid overwriting position after battle)
  const initialPositionSynced = useRef(false);
  // Track the gameState version that we've synced position from (to detect fresh data)
  const lastSyncedStateVersion = useRef<number>(0);
  // Counter to track gameState updates
  const [stateVersion, setStateVersion] = useState(0);
  // Track if we're waiting for fresh data from chain before initializing
  const [waitingForFreshData, setWaitingForFreshData] = useState(true);

  // Store gameState in ref to avoid dependency issues
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;

  // Store setGameState in ref for callback
  const setGameStateRef = useRef(setGameState);
  setGameStateRef.current = setGameState;

  // Register scenes when engine is ready - use ref to avoid dependency on gameState
  const setupScenes = useCallback((eng: GameEngine) => {
    if (scenesRegistered.current) {
      console.log('[Game] setupScenes: Already registered, skipping');
      return;
    }

    console.log('[Game] setupScenes: Starting scene registration');

    const ctx = eng.getContext();
    console.log('[Game] Got context:', ctx ? 'yes' : 'no');

    // Create sprite manager (shared across scenes)
    if (!spriteManagerRef.current) {
      spriteManagerRef.current = new SpriteManager();
    }
    const spriteManager = spriteManagerRef.current;

    // Create default party for initialization (use ref to get current value)
    const currentGameState = gameStateRef.current;
    const defaultParty: PartyMember[] = currentGameState?.party.map(characterToPartyMember) || [
      { id: 0, name: 'Fighter', class: 0 as any, level: 1, hp: 100, maxHP: 100, mp: 0, maxMP: 0, intelligence: 5, spirit: 5, status: 'OK', experience: 0, learnedSpells: getStartingSpells(0), spellCharges: getStartingSpellCharges(0) },
      { id: 1, name: 'Thief', class: 1 as any, level: 1, hp: 80, maxHP: 80, mp: 0, maxMP: 0, intelligence: 5, spirit: 5, status: 'OK', experience: 0, learnedSpells: getStartingSpells(1), spellCharges: getStartingSpellCharges(1) },
      { id: 2, name: 'W.Mage', class: 4 as any, level: 1, hp: 60, maxHP: 60, mp: 20, maxMP: 20, intelligence: 18, spirit: 18, status: 'OK', experience: 0, learnedSpells: getStartingSpells(4), spellCharges: getStartingSpellCharges(4) },
      { id: 3, name: 'B.Mage', class: 5 as any, level: 1, hp: 50, maxMP: 25, mp: 25, maxHP: 50, intelligence: 20, spirit: 20, status: 'OK', experience: 0, learnedSpells: getStartingSpells(5), spellCharges: getStartingSpellCharges(5) },
    ];

    console.log('[Game] Creating scenes...');

    // Create and register all scenes with required constructor args
    const worldScene = new WorldScene(ctx, spriteManager);
    const battleScene = new BattleScene(ctx, spriteManager, defaultParty);
    const menuScene = new MenuScene(ctx);

    // Create TownScene with default Cornelia town
    const defaultTown = getTownById('CORNELIA');
    const townScene = defaultTown
      ? new TownScene(ctx, spriteManager, defaultTown, StoryLocation.CORNELIA)
      : null;

    // Create DungeonScene with default Temple of Fiends
    const dungeonScene = new DungeonScene(ctx, spriteManager, { dungeonId: 'TEMPLE_OF_FIENDS' });

    // Create ShopScene for buying/selling items
    const shopScene = new ShopScene(
      ctx,
      BuildingType.ITEM_SHOP,
      'Shop',
      [],
      currentGameState?.gil || 500
    );

    // Create QuestManager for story progression
    if (!questManagerRef.current) {
      questManagerRef.current = new QuestManager();
    }
    const questManager = questManagerRef.current;

    // Set up QuestManager flag callback to grant transports
    questManager.onFlagSetCallback((flag: StoryFlag) => {
      console.log(`[Game] Quest flag set: ${StoryFlag[flag]}`);

      // Grant transports based on story flags
      if (flag === StoryFlag.GOT_SHIP) {
        worldScene.grantShip(20, 33); // Pravoka dock location
        console.log('[Game] Ship granted!');
      } else if (flag === StoryFlag.GOT_CANOE) {
        worldScene.grantCanoe();
        console.log('[Game] Canoe granted!');
      } else if (flag === StoryFlag.GOT_AIRSHIP) {
        worldScene.grantAirship(42, 52); // Desert altar location
        console.log('[Game] Airship granted!');
      }
    });

    console.log('[Game] Registering scenes...');
    eng.registerScene(GameScene.WORLD, worldScene);
    eng.registerScene(GameScene.BATTLE, battleScene);
    eng.registerScene(GameScene.MENU, menuScene);
    if (townScene) {
      eng.registerScene(GameScene.TOWN, townScene);
    }
    eng.registerScene(GameScene.DUNGEON, dungeonScene);
    eng.registerScene(GameScene.SHOP, shopScene);

    // IMPORTANT: Set initial position BEFORE switching to world scene
    // This ensures the saved position is used instead of the default center position
    if (currentGameState) {
      console.log('[Game] Setting initial position from gameState:', currentGameState.worldX, currentGameState.worldY);
      worldScene.setPosition(currentGameState.worldX, currentGameState.worldY);
    }

    // Set starter inventory for battle scene
    // Item IDs from ItemData.ts: 40=Potion, 41=Hi-Potion, 45=Phoenix Down
    const starterInventory = new Map<number, number>();
    starterInventory.set(40, 5);  // 5 Potions
    starterInventory.set(41, 2);  // 2 Hi-Potions
    starterInventory.set(45, 2);  // 2 Phoenix Downs
    battleScene.setInventory(starterInventory);

    // Initialize MenuScene with party and inventory data
    if (currentGameState?.party) {
      menuScene.setParty([...currentGameState.party]);
    }
    if (currentGameState?.inventory && currentGameState?.inventoryQty) {
      menuScene.setInventory(currentGameState.inventory, currentGameState.inventoryQty);
    } else {
      // Starter inventory for menu scene (same as battle scene)
      const starterIds = [40, 41, 45];
      const starterQty = [5, 2, 2];
      menuScene.setInventory(starterIds, starterQty);
    }

    // ===================================================================
    // WorldScene Callbacks
    // ===================================================================

    // Random encounter callback
    worldScene.onEncounter((enemies: BattleEnemy[], zone: string) => {
      console.log(`[Game] Random encounter! Zone: ${zone}, Enemies:`, enemies.map(e => e.name));

      // Get current party from ref
      const currentState = gameStateRef.current;
      const partyMembers = currentState?.party.map(characterToPartyMember) || defaultParty;

      // Start battle through GameEngine (handles transition properly)
      eng.startBattle(enemies, zone, partyMembers);
    });

    // Location entry callback (town/dungeon)
    worldScene.onEnterLocation((location: any) => {
      console.log(`[Game] Entering location: ${location.name} (${location.type}) id=${location.id}`);

      // Towns and castles use TownScene
      if (location.type === 'town' || location.type === 'castle') {
        const town = getTownById(location.id);
        if (town && townScene) {
          // Map location ID to StoryLocation enum
          const storyLocation = (StoryLocation as any)[location.id] || StoryLocation.CORNELIA;
          townScene.loadTown(town, storyLocation);
          eng.enterTown(location.name);
        } else {
          console.warn(`[Game] Town not found: ${location.id}`);
        }
      }
      // Dungeons, caves, and shrines use DungeonScene
      else if (location.type === 'dungeon' || location.type === 'cave' || location.type === 'shrine') {
        dungeonScene.loadDungeon(location.id);
        eng.enterDungeon(location.name);
      }
    });

    // Set up exit callbacks for TownScene and DungeonScene
    if (townScene) {
      townScene.onExit(() => {
        console.log('[Game] Exiting town');
        eng.exitTown();
      });
    }

    dungeonScene.onExit(() => {
      console.log('[Game] Exiting dungeon');
      eng.exitDungeon();
    });

    // Track if current battle is from a trapped chest
    let isTrappedChestBattle = false;

    // Trapped chest battle callback
    dungeonScene.onTrappedChestBattle((enemyIds: number[], treasure) => {
      console.log(`[Game] Trapped chest! Enemies:`, enemyIds, 'Treasure:', treasure.name);

      // Convert enemy IDs to BattleEnemy objects
      const enemies = enemyIds.map((id, index) => enemyIdToBattleEnemy(id, index));

      // Get current party
      const currentState = gameStateRef.current;
      const partyMembers = currentState?.party.map(characterToPartyMember) || defaultParty;

      // Mark as trapped chest battle
      isTrappedChestBattle = true;

      // Start battle
      eng.startBattle(enemies, 'trapped_chest', partyMembers);
    });

    // Treasure open callback (for non-trapped chests or after battle victory)
    dungeonScene.onTreasure((treasure) => {
      console.log(`[Game] Treasure collected:`, treasure.name, treasure.type);

      const currentState = gameStateRef.current;
      if (!currentState) return;

      let newState = { ...currentState };

      switch (treasure.type) {
        case 'GIL':
          // Add gil
          newState.gil = currentState.gil + (treasure.amount || 0);
          console.log(`[Game] Added ${treasure.amount} gil. Total: ${newState.gil}`);
          break;

        case 'ITEM':
        case 'EQUIPMENT':
          // Add item to inventory
          if (treasure.itemId) {
            // Ensure inventory arrays are properly sized to 64 slots
            const newInventory = [...(currentState.inventory || [])];
            const newInventoryQty = [...(currentState.inventoryQty || [])];

            // Pad arrays to 64 slots if needed
            while (newInventory.length < 64) newInventory.push(0);
            while (newInventoryQty.length < 64) newInventoryQty.push(0);

            const quantity = treasure.amount || 1;

            // Find existing slot or empty slot
            const existingIndex = newInventory.findIndex(id => id === treasure.itemId);
            if (existingIndex !== -1) {
              // Add to existing stack
              newInventoryQty[existingIndex] = (newInventoryQty[existingIndex] || 0) + quantity;
              console.log(`[Game] Stacked ${quantity}x ${treasure.name} (ID: ${treasure.itemId}) to slot ${existingIndex}, total: ${newInventoryQty[existingIndex]}`);
            } else {
              // Find empty slot (slot with itemId = 0 or undefined)
              const emptyIndex = newInventory.findIndex(id => !id || id === 0);
              if (emptyIndex !== -1) {
                newInventory[emptyIndex] = treasure.itemId;
                newInventoryQty[emptyIndex] = quantity;
                console.log(`[Game] Added ${quantity}x ${treasure.name} (ID: ${treasure.itemId}) to slot ${emptyIndex}`);
              } else {
                console.warn('[Game] Inventory full! Could not add:', treasure.name);
                return;
              }
            }

            newState.inventory = newInventory;
            newState.inventoryQty = newInventoryQty;
          }
          break;

        case 'KEY_ITEM':
          // Add key item to key items bitfield
          if (treasure.itemId) {
            // Key items use a bitmask - IDs 100+ map to bit positions 0+
            newState.keyItems = addKeyItem(currentState.keyItems, treasure.itemId);
            console.log(`[Game] Added key item: ${treasure.name} (ID ${treasure.itemId})`);
          }
          break;

        case 'SPELL':
          // Spells would be learned by a character
          // For now, just log - would need to pick which character learns it
          console.log(`[Game] Found spell: ${treasure.name} - spell learning not yet implemented`);
          break;
      }

      // Update game state
      setGameStateRef.current(newState);
    });

    // ===================================================================
    // TownScene Building Entry Callbacks
    // ===================================================================

    if (townScene) {
      townScene.onEnterBuilding((building: Building) => {
        console.log(`[Game] Entering building: ${building.name} (${building.type})`);

        switch (building.type) {
          case BuildingType.WEAPON_SHOP:
          case BuildingType.ARMOR_SHOP:
          case BuildingType.ITEM_SHOP:
          case BuildingType.MAGIC_SHOP:
            // Configure shop scene for this building
            shopScene.loadShop(building.type, building.name, building.inventory || []);
            shopScene.setPlayerGil(gameStateRef.current?.gil || 0);
            eng.setScene(GameScene.SHOP);
            break;

          case BuildingType.INN:
            // Handle inn rest
            handleInnRest(building.price || 50);
            break;

          case BuildingType.CLINIC:
            // Handle clinic revival
            handleClinic(building.price || 100);
            break;

          case BuildingType.HOUSE:
            // Regular houses - just NPC interaction, already handled
            console.log('[Game] Entered house - NPCs available');
            break;
        }
      });
    }

    // ===================================================================
    // ShopScene Callbacks
    // ===================================================================

    // Shop close - return to town
    shopScene.onClose(() => {
      console.log('[Game] Shop closed, returning to town');
      eng.setScene(GameScene.TOWN);
    });

    // Shop purchase - deduct gil and add item to inventory
    shopScene.onPurchase((item: ShopItem, quantity: number) => {
      const currentState = gameStateRef.current;
      if (!currentState) return;

      const totalCost = item.price * quantity;
      if (currentState.gil >= totalCost) {
        console.log(`[Game] Purchased ${quantity}x ${item.name} for ${totalCost} gil`);

        // Update gil
        const newGil = currentState.gil - totalCost;

        // Add item to inventory
        const newInventory = [...currentState.inventory];
        const newInventoryQty = [...currentState.inventoryQty];

        // Find existing slot or empty slot
        const existingIndex = newInventory.findIndex(id => id === item.id);
        if (existingIndex !== -1) {
          // Add to existing stack
          newInventoryQty[existingIndex] = (newInventoryQty[existingIndex] || 0) + quantity;
        } else {
          // Find empty slot
          const emptyIndex = newInventory.findIndex(id => id === 0);
          if (emptyIndex !== -1) {
            newInventory[emptyIndex] = item.id;
            newInventoryQty[emptyIndex] = quantity;
          } else {
            console.warn('[Game] Inventory full!');
            return;
          }
        }

        const newState: GameState = {
          ...currentState,
          gil: newGil,
          inventory: newInventory,
          inventoryQty: newInventoryQty,
        };

        setGameStateRef.current(newState);
        shopScene.setPlayerGil(newGil);
        console.log('[Game] Purchase complete - Gil:', newGil);
      } else {
        console.log('[Game] Not enough gil!');
      }
    });

    // Shop sell - add gil and remove item from inventory
    shopScene.onSell((item: ShopItem, quantity: number) => {
      const currentState = gameStateRef.current;
      if (!currentState) return;

      const sellPrice = Math.floor(item.price / 2) * quantity;
      console.log(`[Game] Sold ${quantity}x ${item.name} for ${sellPrice} gil`);

      // Update gil
      const newGil = currentState.gil + sellPrice;

      // Remove from inventory
      const newInventory = [...currentState.inventory];
      const newInventoryQty = [...currentState.inventoryQty];

      const itemIndex = newInventory.findIndex(id => id === item.id);
      if (itemIndex !== -1) {
        newInventoryQty[itemIndex] = (newInventoryQty[itemIndex] || 0) - quantity;
        if (newInventoryQty[itemIndex] <= 0) {
          newInventory[itemIndex] = 0;
          newInventoryQty[itemIndex] = 0;
        }
      }

      const newState: GameState = {
        ...currentState,
        gil: newGil,
        inventory: newInventory,
        inventoryQty: newInventoryQty,
      };

      setGameStateRef.current(newState);
      shopScene.setPlayerGil(newGil);
      console.log('[Game] Sale complete - Gil:', newGil);
    });

    // Helper function for inn rest
    const handleInnRest = (price: number) => {
      const currentState = gameStateRef.current;
      if (!currentState) return;

      if (currentState.gil < price) {
        console.log('[Game] Not enough gil for inn!');
        // TODO: Show message to player
        return;
      }

      // Restore all party members
      const restoredParty = currentState.party.map(char => ({
        ...char,
        currentHP: char.maxHP,
        currentMP: char.maxMP,
        status: 0, // Clear status effects
      })) as [Character, Character, Character, Character];

      const newState: GameState = {
        ...currentState,
        party: restoredParty,
        gil: currentState.gil - price,
      };

      setGameStateRef.current(newState);
      console.log(`[Game] Inn rest complete - HP/MP restored, ${price} gil spent`);
      // TODO: Show "HP and MP restored!" message
    };

    // Helper function for clinic revival
    const handleClinic = (pricePerRevive: number) => {
      const currentState = gameStateRef.current;
      if (!currentState) return;

      // Count dead party members
      const deadCount = currentState.party.filter(char => char.currentHP <= 0).length;

      if (deadCount === 0) {
        console.log('[Game] No one needs revival');
        // TODO: Show message
        return;
      }

      const totalCost = deadCount * pricePerRevive;

      if (currentState.gil < totalCost) {
        console.log('[Game] Not enough gil for clinic!');
        // TODO: Show message
        return;
      }

      // Revive all dead members with 1 HP
      const revivedParty = currentState.party.map(char => ({
        ...char,
        currentHP: char.currentHP <= 0 ? 1 : char.currentHP,
      })) as [Character, Character, Character, Character];

      const newState: GameState = {
        ...currentState,
        party: revivedParty,
        gil: currentState.gil - totalCost,
      };

      setGameStateRef.current(newState);
      console.log(`[Game] Clinic revival complete - ${deadCount} members revived, ${totalCost} gil spent`);
    };

    // ===================================================================
    // BattleScene Callbacks
    // ===================================================================

    // Battle end callback - handles victory/defeat/level ups
    battleScene.onBattleEnd((result: BattleResult) => {
      console.log('[Game] Battle ended:', result);

      // Sync battle results back to game state
      const currentState = gameStateRef.current;
      if (currentState && result.updatedParty && result.updatedInventory) {
        // Create updated game state
        const updatedParty = currentState.party.map((char, i) => {
          const partyMember = result.updatedParty[i];
          return partyMember ? partyMemberToCharacter(partyMember, char) : char;
        }) as [Character, Character, Character, Character];

        // Convert inventory map back to arrays
        const { inventory, inventoryQty } = mapToInventoryArrays(result.updatedInventory);

        const newState: GameState = {
          ...currentState,
          party: updatedParty,
          gil: currentState.gil + (result.victory ? result.pasGained : 0), // Add PAS on victory
          inventory,
          inventoryQty,
        };

        // Update local state immediately for responsive UI
        setGameStateRef.current(newState);
        console.log('[Game] State synced - PAS:', newState.gil, 'Party HP:', updatedParty.map(p => p.currentHP));
      }

      // If this was a trapped chest battle and player won, award the treasure
      if (isTrappedChestBattle && result.victory) {
        console.log('[Game] Trapped chest battle won - awarding treasure');
        dungeonScene.awardPendingTreasure();
        isTrappedChestBattle = false;
      } else if (isTrappedChestBattle) {
        // Player lost or fled - no treasure
        console.log('[Game] Trapped chest battle lost - no treasure');
        isTrappedChestBattle = false;
      }

      eng.returnToWorld(result);
    });

    // ===================================================================
    // MenuScene Callbacks
    // ===================================================================

    // Menu close callback - sync party/inventory state when menu closes
    menuScene.onMenuClose((result: MenuStateResult) => {
      console.log('[Game] Menu closed, syncing state...');

      const currentState = gameStateRef.current;
      if (currentState && result.party && result.inventory) {
        // Map menu Character[] back to game state party tuple
        const updatedParty = result.party.slice(0, 4) as [Character, Character, Character, Character];

        // Pad inventory arrays to 64 slots
        const inventory = [...result.inventory.itemIds];
        const inventoryQty = [...result.inventory.quantities];
        while (inventory.length < 64) {
          inventory.push(0);
          inventoryQty.push(0);
        }

        const newState: GameState = {
          ...currentState,
          party: updatedParty,
          inventory,
          inventoryQty,
        };

        setGameStateRef.current(newState);
        console.log('[Game] Menu state synced - Party HP:', updatedParty.map(p => p.currentHP));
      }
    });

    // ===================================================================
    // Menu Open Callback - Sync Party Data
    // ===================================================================

    // Register callback to sync party data when menu opens (via Enter key)
    eng.onMenuOpen(() => {
      const currentState = gameStateRef.current;
      if (currentState) {
        console.log('[Game] Menu opening - syncing party data, HP:', currentState.party.map(p => p.currentHP));
        menuScene.setParty([...currentState.party]);
        if (currentState.inventory && currentState.inventoryQty) {
          menuScene.setInventory(currentState.inventory, currentState.inventoryQty);
        }
        menuScene.setKeyItems(currentState.keyItems || 0);
      }
    });

    // ===================================================================
    // Initial Scene
    // ===================================================================

    // Start with world scene
    console.log('[Game] Switching to WORLD scene...');
    eng.switchScene(GameScene.WORLD);

    scenesRegistered.current = true;
    console.log('[Game] Scene registration complete');
  }, []); // No dependencies - uses refs

  // Handle engine ready callback
  const onEngineReady = useCallback((eng: GameEngine) => {
    console.log('[Game] onEngineReady called, scenesRegistered:', scenesRegistered.current);
    handleEngineReady(eng);
    // Reset the flag in case this is a re-render (React Strict Mode)
    scenesRegistered.current = false;
    setupScenes(eng);
  }, [handleEngineReady, setupScenes]);

  // Cleanup on unmount - reset the scenesRegistered flag
  useEffect(() => {
    return () => {
      console.log('[Game] Unmounting - resetting scenesRegistered');
      scenesRegistered.current = false;
    };
  }, []);

  // Poll live position from engine
  useEffect(() => {
    if (!engine || !isEngineReady) return;

    const interval = setInterval(() => {
      const pos = engine.getLivePosition();
      setLivePosition(pos);
    }, 100); // Update 10 times per second

    return () => clearInterval(interval);
  }, [engine, isEngineReady]);

  // Force refresh from blockchain on mount to get latest saved state
  const hasRefreshedFromChain = useRef(false);
  useEffect(() => {
    if (hasRefreshedFromChain.current) return;
    if (!isConnected) return;

    hasRefreshedFromChain.current = true;
    // Reset position sync flag so fresh data from chain gets applied
    initialPositionSynced.current = false;
    console.log('[Game] Force refreshing from blockchain on mount...');

    // Call refresh - when it completes, gameState will update and trigger position sync
    refreshState(true).then(() => {
      // Increment state version to signal fresh data arrived
      setStateVersion(v => v + 1);
      setWaitingForFreshData(false);
      console.log('[Game] Refresh complete, fresh data received, waitingForFreshData = false');
    }).catch((err) => {
      console.error('[Game] Refresh failed:', err);
      setWaitingForFreshData(false); // Still allow game to load
    });
  }, [isConnected, refreshState]);

  // Update engine with game state when it changes
  useEffect(() => {
    if (engine && gameState) {
      console.log('[Game] gameState changed - position:', gameState.worldX, gameState.worldY, 'stateVersion:', stateVersion, 'lastSynced:', lastSyncedStateVersion.current);
      engine.loadGameState(gameState);

      // Sync position when we have fresh data from chain (stateVersion changed)
      // After initial sync, WorldScene tracks position locally (to preserve position after battle)

      // Only sync if we haven't synced this version yet
      if (stateVersion > lastSyncedStateVersion.current) {
        engine.setWorldPosition(gameState.worldX, gameState.worldY);
        lastSyncedStateVersion.current = stateVersion;
        initialPositionSynced.current = true;
        console.log('[Game] Position synced from fresh chain data (v' + stateVersion + '):', gameState.worldX, gameState.worldY);
      } else if (!initialPositionSynced.current && stateVersion === 0) {
        // First load before refresh completes - still sync so we have something to show
        engine.setWorldPosition(gameState.worldX, gameState.worldY);
        initialPositionSynced.current = true;
        console.log('[Game] Initial position synced (pre-refresh):', gameState.worldX, gameState.worldY);
      }

      // Update MenuScene with party, inventory, and key items data
      const menuScene = engine.getScene<MenuScene>(GameScene.MENU);
      if (menuScene) {
        menuScene.setParty([...gameState.party]);
        if (gameState.inventory && gameState.inventoryQty) {
          menuScene.setInventory(gameState.inventory, gameState.inventoryQty);
        }
        menuScene.setKeyItems(gameState.keyItems || 0);
      }
    }
  }, [engine, gameState, stateVersion]);

  // Update engine with combat state when it changes
  useEffect(() => {
    if (engine && combatState) {
      engine.loadCombatState(combatState);

      // Switch to battle scene if combat is active
      if (combatState.active && engine.getCurrentScene() !== GameScene.BATTLE) {
        const battleScene = engine['scenes']?.get(GameScene.BATTLE) as BattleScene | undefined;
        if (battleScene && gameState && typeof battleScene.startBattle === 'function') {
          // Convert enemy IDs to BattleEnemy objects
          const enemyIds = combatState.enemyIds.filter((id: number) => id > 0);
          const enemies = enemyIds.map(enemyIdToBattleEnemy);
          const partyMembers = gameState.party.map(characterToPartyMember);
          battleScene.startBattle(enemies, partyMembers);
        }
        engine.setScene(GameScene.BATTLE);
      }
    } else if (engine && !combatState?.active && engine.getCurrentScene() === GameScene.BATTLE) {
      // Combat ended, return to world
      engine.setScene(GameScene.WORLD);
    }
  }, [engine, combatState, gameState]);

  // Redirect if not connected or no game - only check once loading is complete AND fetch has been attempted
  const hasRedirectedRef = useRef(false);
  useEffect(() => {
    // Don't redirect while loading
    if (isLoading) return;
    // Don't redirect until we've actually tried to fetch game state
    if (!hasAttemptedFetch) return;
    // Don't redirect multiple times
    if (hasRedirectedRef.current) return;

    if (!isConnected) {
      hasRedirectedRef.current = true;
      navigate('/');
    } else if (!hasGame) {
      hasRedirectedRef.current = true;
      navigate('/new-game');
    }
  }, [isConnected, hasGame, isLoading, hasAttemptedFetch, navigate]);

  // Handle keyboard shortcuts for scene switching (dev mode)
  useEffect(() => {
    if (!engine || process.env.NODE_ENV !== 'development') return;

    const handleDevKeys = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Toggle menu
        if (engine.getCurrentScene() === GameScene.MENU) {
          engine.setScene(GameScene.WORLD);
        } else if (engine.getCurrentScene() === GameScene.WORLD) {
          // Ensure MenuScene has latest party data before opening
          const currentState = gameStateRef.current;
          const menuScene = engine.getScene<MenuScene>(GameScene.MENU);
          if (menuScene && currentState) {
            menuScene.setParty([...currentState.party]);
            if (currentState.inventory && currentState.inventoryQty) {
              menuScene.setInventory(currentState.inventory, currentState.inventoryQty);
            }
            menuScene.setKeyItems(currentState.keyItems || 0);
          }
          engine.setScene(GameScene.MENU);
        }
      }
    };

    window.addEventListener('keydown', handleDevKeys);
    return () => window.removeEventListener('keydown', handleDevKeys);
  }, [engine]);

  // Load slots for save modal
  const loadSlotsForSave = useCallback(async () => {
    setIsLoadingSlots(true);
    try {
      const slotInfo = await getSlotInfo();
      if (!slotInfo) {
        setSlots(Array(8).fill({ active: false, state: null }));
        return;
      }

      setCurrentSlot(slotInfo.current);

      // Fetch game states for all active slots
      const slotData: SlotData[] = await Promise.all(
        slotInfo.slots.map(async (active, index) => {
          if (!active) {
            return { active: false, state: null };
          }
          try {
            const state = await getGameStateForSlot(index);
            return { active: true, state };
          } catch {
            return { active: true, state: null };
          }
        })
      );

      setSlots(slotData);
    } catch (error) {
      console.error('Failed to load slots:', error);
      setSlots(Array(8).fill({ active: false, state: null }));
    } finally {
      setIsLoadingSlots(false);
    }
  }, [getSlotInfo, getGameStateForSlot]);

  // Open save modal
  const openSaveModal = useCallback(() => {
    if (isInCombat) return;
    loadSlotsForSave();
    setShowSaveModal(true);
  }, [isInCombat, loadSlotsForSave]);

  // Handle saving game to blockchain (to specific slot)
  const handleSaveToSlot = useCallback(async (targetSlot: number) => {
    if (!engine || !gameState || isInCombat) {
      console.log('[Game] Cannot save: engine/gameState missing or in combat');
      return;
    }

    setSaveStatus('saving');
    setShowSaveModal(false);

    try {
      // If saving to a different slot, switch to it first
      if (targetSlot !== currentSlot) {
        const switchResult = await selectSlot(targetSlot);
        if (!switchResult.success) {
          console.error('[Game] Failed to switch slot:', switchResult.error);
          setSaveStatus('error');
          setTimeout(() => setSaveStatus('idle'), 3000);
          return;
        }
        setCurrentSlot(targetSlot);
      }

      // Get current game state with synced position from active scene
      const currentState = engine.getCurrentGameState();

      if (!currentState) {
        console.error('[Game] getCurrentGameState returned null');
        setSaveStatus('error');
        return;
      }

      // Ensure we use the latest React gameState for inventory/keyItems
      const stateToSave = {
        ...currentState,
        inventory: gameState.inventory,
        inventoryQty: gameState.inventoryQty,
        keyItems: gameState.keyItems,
        gil: gameState.gil,
      };

      console.log('[Game] Saving to slot', targetSlot, 'with position:', {
        x: stateToSave.worldX,
        y: stateToSave.worldY,
        gil: stateToSave.gil,
        inventoryCount: stateToSave.inventory?.filter((id: number) => id > 0).length || 0,
      });

      const result = await saveGameToChain(stateToSave);

      if (result.success) {
        setSaveStatus('success');
        setTimeout(() => setSaveStatus('idle'), 3000);
      } else {
        console.error('[Game] Save failed:', result.error);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (err) {
      console.error('[Game] Save error:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [engine, gameState, isInCombat, currentSlot, selectSlot, saveGameToChain]);

  // Format play time helper
  const formatPlayTime = (blocks: number) => {
    const seconds = blocks * 6;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  if (isLoading || waitingForFreshData || !hasAttemptedFetch) {
    return (
      <div className="min-h-screen w-full bg-[#1a1a2e] flex items-center justify-center">
        <LoadingSpinner size="lg" text={waitingForFreshData || !hasAttemptedFetch ? "Loading saved game from blockchain..." : "Loading game..."} fullScreen />
      </div>
    );
  }

  if (!gameState) {
    return null;
  }

  return (
    <div className="h-screen w-full bg-[#1a1a2e] flex flex-col overflow-hidden">
      {/* Header - compact */}
      <header className="bg-[#0f3460] border-b-2 border-[#533483] px-3 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-pixel-sm text-[#e94560]">Crystals of Polkadot</h1>
          <div className="flex items-center gap-2">
            <NetworkBadge />
            <WalletButton />
          </div>
        </div>
      </header>

      {/* Main game area */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Left sidebar - Currency, Party, Combat */}
        <aside className="w-52 xl:w-60 bg-[#16213e] border-r-2 border-[#533483] p-2 overflow-y-auto flex-shrink-0 hidden md:flex md:flex-col gap-2">
          {/* Currency display */}
          <Card className="card-compact">
            <div className="flex items-center justify-between">
              <span className="text-gray-300 text-game text-sm">PAS</span>
              <span className="text-[#ffd700] text-game text-lg font-bold">{gameState.gil.toLocaleString()}</span>
            </div>
          </Card>

          {/* Party Status - takes remaining space */}
          <Card title="Party" className="card-compact flex-1 min-h-0 overflow-hidden">
            <div className="space-y-1.5 overflow-y-auto h-full">
              {gameState.party.map((character, index) => {
                const hpPercent = character.maxHP > 0 ? (character.currentHP / character.maxHP) * 100 : 0;
                const hpColorClass = hpPercent <= 25 ? 'text-red-400' : hpPercent <= 50 ? 'text-yellow-400' : 'text-[#00ff00]';
                const hpBarClass = hpPercent <= 25 ? 'hp-bar-critical' : hpPercent <= 50 ? 'hp-bar-warning' : 'hp-bar';

                return (
                  <div key={index} className="ff-border bg-[#0f3460] p-2 rounded">
                    {/* Header with name + level */}
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-bold text-[#e94560] text-game text-xs">
                        {CLASS_INFO[character.class]?.name || `Hero ${index + 1}`}
                      </span>
                      <span className="text-[#ffd700] text-game text-xs">Lv.{character.level}</span>
                    </div>

                    {/* HP Bar */}
                    <div className="mb-1">
                      <div className="flex justify-between text-game text-xs mb-0.5 text-gray-300">
                        <span>HP</span>
                        <span className={hpColorClass}>
                          {character.currentHP}/{character.maxHP}
                        </span>
                      </div>
                      <div className="h-2 bg-[#1a1a2e] rounded overflow-hidden">
                        <div
                          className={`${hpBarClass} h-full transition-all duration-300`}
                          style={{ width: `${hpPercent}%` }}
                        />
                      </div>
                    </div>

                    {/* MP Bar */}
                    <div>
                      <div className="flex justify-between text-game text-xs mb-0.5 text-gray-300">
                        <span>MP</span>
                        <span className="text-[#00aaff]">{character.currentMP}/{character.maxMP}</span>
                      </div>
                      <div className="h-2 bg-[#1a1a2e] rounded overflow-hidden">
                        <div
                          className="mp-bar h-full transition-all duration-300"
                          style={{ width: `${character.maxMP > 0 ? (character.currentMP / character.maxMP) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Combat actions - only during combat */}
          {isInCombat && combatState && (
            <Card title="Combat" className="card-compact bg-red-900/20">
              <p className="text-game text-xs text-gray-300 mb-2">
                Enemies: <span className="text-[#e94560] font-bold">{combatState.enemyIds.filter((id: number) => id > 0).length}</span>
              </p>
              <div className="space-y-1.5">
                <Button className="w-full" size="sm" variant="danger">Attack (Z)</Button>
                <Button className="w-full" size="sm" variant="secondary">Flee (X)</Button>
              </div>
            </Card>
          )}
        </aside>

        {/* Center - Game canvas */}
        <main className="flex-1 flex flex-col min-w-0">
          {/* Game canvas - fills available space */}
          <div className="flex-1 bg-[#0f0e17] flex items-center justify-center relative min-h-0">
            <GameCanvas
              width={256}
              height={240}
              scale={3}
              onEngineReady={onEngineReady}
              className="game-canvas w-full h-full"
            />

            {/* Engine loading overlay */}
            {!isEngineReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-80">
                <LoadingSpinner text="Initializing engine..." />
              </div>
            )}
          </div>

          {/* Status bar - compact */}
          <div className="bg-[#16213e] border-t-2 border-[#533483] px-3 py-1.5 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="text-game text-xs text-gray-400">
                <span className="text-[#ffd700]">{livePosition.scene}</span>
                <span className="mx-2">•</span>
                <span className="text-[#e94560]">({livePosition.x}, {livePosition.y})</span>
              </div>
              <div className="text-game text-xs text-[#eaeaea]">
                {isInCombat ? '⚔️ Combat' : '🌍 Exploring'}
              </div>
            </div>
          </div>
        </main>

        {/* Right sidebar - Actions, Inventory, Key Items, Info */}
        <aside className="w-48 xl:w-56 bg-[#16213e] border-l-2 border-[#533483] p-2 overflow-y-auto flex-shrink-0 hidden md:flex md:flex-col gap-2">
          {/* Actions */}
          <Card title="Actions" className="card-compact">
            <div className="space-y-1.5">
              <Button
                className="w-full"
                size="sm"
                variant={saveStatus === 'success' ? 'success' : saveStatus === 'error' ? 'danger' : 'primary'}
                disabled={isInCombat || saveStatus === 'saving' || isSaving}
                onClick={openSaveModal}
              >
                {saveStatus === 'saving' || isSaving
                  ? '⏳ Saving...'
                  : saveStatus === 'success'
                  ? '✓ Saved!'
                  : saveStatus === 'error'
                  ? '✗ Failed'
                  : 'Save Game'}
              </Button>
              <Button
                className="w-full"
                variant="secondary"
                size="sm"
                disabled={
                  isInCombat ||
                  engine?.getCurrentScene() === GameScene.MENU ||
                  engine?.getCurrentScene() === GameScene.SHOP
                }
                onClick={() => {
                  // Ensure MenuScene has latest party data before opening
                  const menuScene = engine?.getScene<MenuScene>(GameScene.MENU);
                  if (menuScene && gameState) {
                    menuScene.setParty([...gameState.party]);
                    if (gameState.inventory && gameState.inventoryQty) {
                      menuScene.setInventory(gameState.inventory, gameState.inventoryQty);
                    }
                    menuScene.setKeyItems(gameState.keyItems || 0);
                  }
                  engine?.setScene(GameScene.MENU);
                }}
              >
                Menu
              </Button>
              <Button
                className="w-full"
                variant="secondary"
                size="sm"
                onClick={() => setShowHomeConfirm(true)}
              >
                ← Home
              </Button>
            </div>
          </Card>

          {/* Inventory */}
          <Card title="Inventory" className="card-compact">
            {gameState.inventory && gameState.inventory.some(id => id > 0) ? (
              <ul className="text-game text-xs max-h-32 overflow-y-auto space-y-0.5">
                {gameState.inventory.map((itemId, i) => {
                  if (itemId <= 0) return null;
                  const item = getItem(itemId);
                  const qty = gameState.inventoryQty?.[i] || 1;
                  return (
                    <li key={i} className="flex justify-between text-gray-300">
                      <span className="truncate">{item?.name || `#${itemId}`}</span>
                      <span className="text-[#ffd700] ml-1">x{qty}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-game text-xs text-gray-500">Empty</p>
            )}
          </Card>

          {/* Key Items */}
          <Card title="Key Items" className="card-compact">
            {gameState.keyItems > 0 ? (
              <ul className="text-game text-xs max-h-24 overflow-y-auto space-y-0.5">
                {getOwnedKeyItems(gameState.keyItems).map((keyItem, i) => (
                  <li key={i} className="text-gray-300 truncate">
                    {keyItem.name}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-game text-xs text-gray-500">None</p>
            )}
          </Card>

          {/* Info */}
          <Card className="card-compact" title="Controls">
            <div className="text-game text-xs text-gray-400 space-y-0.5">
              <div><span className="text-white">↑↓←→</span> Move</div>
              <div><span className="text-white">Z/X</span> OK/Cancel</div>
              <div><span className="text-white">Enter</span> Menu</div>
              <div><span className="text-white">ESC</span> Exit</div>
            </div>
          </Card>
        </aside>
      </div>

      {/* Home Confirmation Modal */}
      <Modal
        isOpen={showHomeConfirm}
        onClose={() => setShowHomeConfirm(false)}
        title="Return Home?"
      >
        <div className="p-4">
          <p className="text-game text-base text-yellow-400 mb-3">
            Unsaved changes will be lost!
          </p>
          <p className="text-game text-sm text-gray-400 mb-6">
            Any progress since your last save will not be kept. Make sure to save your game before leaving.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowHomeConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setShowHomeConfirm(false);
                openSaveModal();
              }}
            >
              Save First
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => navigate('/')}
            >
              Leave Anyway
            </Button>
          </div>
        </div>
      </Modal>

      {/* Save Game Modal */}
      <Modal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title="Save Game"
      >
        <div className="p-4">
          <p className="text-game text-sm text-gray-400 mb-4">
            Select a slot to save your progress. Current slot is highlighted.
          </p>

          {isLoadingSlots ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner text="Loading slots..." />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 max-h-[32rem] overflow-y-auto">
              {slots.map((slot, index) => {
                const isCurrentSlot = index === currentSlot;

                // Empty slot
                if (!slot.active) {
                  return (
                    <div
                      key={index}
                      className={`ff-border p-3 rounded transition-colors ${
                        isCurrentSlot
                          ? 'bg-[#1a3a1a] border-[#4a4]'
                          : 'bg-[#1a1a2e] border-dashed border-[#533483]/50'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className={`text-game text-xs font-bold ${isCurrentSlot ? 'text-[#4f4]' : 'text-gray-500'}`}>
                          Slot {index + 1} {isCurrentSlot && '*'}
                        </span>
                        <span className="text-game text-xs text-gray-600">
                          Empty
                        </span>
                      </div>
                      <div className="h-12 flex items-center justify-center mb-2">
                        <span className="text-gray-600 text-xl">---</span>
                      </div>
                      <Button
                        variant={isCurrentSlot ? 'primary' : 'secondary'}
                        size="sm"
                        className="w-full"
                        onClick={() => handleSaveToSlot(index)}
                        disabled={!isCurrentSlot}
                      >
                        {isCurrentSlot ? 'Save Here' : 'N/A'}
                      </Button>
                    </div>
                  );
                }

                // Active slot
                return (
                  <div
                    key={index}
                    className={`ff-border p-3 rounded transition-colors ${
                      isCurrentSlot
                        ? 'bg-[#0f4060] border-[#4af]'
                        : 'bg-[#0f3460] hover:bg-[#103870]'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-2 pb-2 border-b border-[#533483]/30">
                      <span className={`text-game text-xs font-bold ${isCurrentSlot ? 'text-[#4af]' : 'text-[#e94560]'}`}>
                        Slot {index + 1} {isCurrentSlot && '*'}
                      </span>
                      <span className="text-game text-xs text-gray-400">
                        {slot.state ? formatPlayTime(slot.state.playTime) : '--'}
                      </span>
                    </div>

                    {slot.state && (
                      <div className="mb-2">
                        <div className="grid grid-cols-2 gap-1 mb-2">
                          {slot.state.party.map((char, i) => (
                            <span
                              key={i}
                              className="text-game text-xs bg-[#1a1a2e] px-2 py-1 rounded text-center truncate"
                            >
                              {CLASS_INFO[char.class]?.name || '???'}
                            </span>
                          ))}
                        </div>
                        <div className="flex justify-between text-game text-xs text-gray-300">
                          <span>Lv. <span className="text-white">{slot.state.party[0]?.level || 1}</span></span>
                          <span><span className="text-[#ffd700]">{slot.state.gil.toLocaleString()}</span> PAS</span>
                        </div>
                      </div>
                    )}

                    <Button
                      variant={isCurrentSlot ? 'primary' : 'secondary'}
                      size="sm"
                      className="w-full"
                      onClick={() => handleSaveToSlot(index)}
                    >
                      {isCurrentSlot ? 'Save' : 'Switch & Save'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end mt-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowSaveModal(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Game;
